"use client";
import React, { FC, useState, useEffect } from "react";
import { useUser } from "@clerk/nextjs";
import { useRouter } from "next/navigation";
import { proPricingTiers } from "@/constants";
import axios from "axios";
import { redirect } from "next/navigation";
import { Slider } from "@/components/ui/slider";
import { Icons } from "@/components/(new)/shared/helper/Icons";
import Link from "next/link";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PricingFeature {
  count: number;
  label: string;
}

interface PricingCardProps {
  type: "free" | "pro";
  price: number;
  credits: number;
  features: PricingFeature[];
  hasRollover?: boolean;
  isCurrentPlan?: boolean;
  onSubscribe?: () => void;
  subscriptionStatus?: string;
  setSliderValue?: (value: number) => void;
}

const PricingFeature: FC<{ count: number; label: string }> = ({
  count,
  label,
}) => (
  <div className="flex-start gap-2.5 overflow-hidden">
    <div className="flex-center gap-2.5 px-1 py-[5px]">
      <Icons.tick />
    </div>
    <div className="">
      <span className="text-xs leading-[17px] font-medium text-[#3dc13c] md:text-sm">
        ~{count}
      </span>
      <span className="text-xs leading-[17px] font-medium text-neutral-100 md:text-sm">
        {" "}
        {label}
      </span>
    </div>
  </div>
);

const PricingCard: FC<PricingCardProps> = ({
  type,
  price,
  credits,
  features,
  hasRollover = false,
  isCurrentPlan = false,
  onSubscribe,
  subscriptionStatus,
  setSliderValue,
}) => {
  const isPro = type === "pro";
  const cardStyles = isPro
    ? "border-[#6cb4ff] bg-gradient-to-b from-[#6cb4ff]/30 to-[#1d7bdb]/30"
    : "border-[#808080] bg-[#1E1E1E]";
  const badgeStyles = isPro
    ? "bg-gradient-to-b from-[#6cb4ff]/30 to-[#1d7bdb]/30 shadow-[0px_0px_6px_0px_rgba(30,123,219,0.80)] outline-[#6cb4ff]"
    : "bg-[#333333] shadow-[0px_0px_4px_0px_rgba(255,255,255,0.24)] outline-[#595959]";

  return (
    <div
      className={`flex h-full flex-col items-end justify-start gap-5 rounded-3xl border-4 ${cardStyles} px-[30px] py-5`}
    >
      <div
        className={`flex-center gap-2.5 overflow-hidden rounded-[10px] text-center text-[10px] leading-3 font-medium text-white ${badgeStyles} px-4 py-[5px] ${!isPro && "opacity-0"} outline-1 outline-offset-[-1px]`}
      >
        {type === "pro" ? "Pro" : "Free"}
      </div>
      <div className="flex flex-col items-center justify-start gap-[50px]">
        <div className="flex flex-col items-start justify-start gap-5">
          <div className="flex items-end justify-start gap-1.5 overflow-hidden">
            <div className="w-3 justify-start self-stretch text-lg leading-snug font-medium text-[#b3b3b3]">
              $
            </div>
            <div className="text-5xl leading-[58px] font-semibold text-white">
              {price}
            </div>
            <div className="text-lg leading-snug font-medium text-[#b3b3b3]">
              /month
            </div>
          </div>

          {isPro && (
            <>
              <div className="h-0 self-stretch opacity-20 outline-1 outline-offset-[-0.50px] outline-white md:hidden" />
              <div className="w-full md:hidden">
                <Select
                  value={price.toString()}
                  onValueChange={(value) => setSliderValue?.(Number(value))}
                >
                  <SelectTrigger className="w-full rounded-xl border border-white/20 bg-gradient-to-b from-[#6cb4ff]/50 to-[#1d7bdb]/40 text-white">
                    <SelectValue placeholder="Select credits" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-[#808080] bg-[#1C63AC]">
                    {[
                      1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000,
                      10000,
                    ].map((value) => (
                      <SelectItem
                        key={value}
                        value={(value / 100).toString()}
                        className="text-white hover:bg-[#3374B6] focus:bg-[#3374B6]"
                      >
                        {value} credits
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>{" "}
            </>
          )}
          <div className="h-0 self-stretch opacity-20 outline-1 outline-offset-[-0.50px] outline-white" />

          <div className="flex flex-col items-start justify-start gap-[30px]">
            <div className="flex flex-col items-start justify-start gap-2">
              <div className="flex-start gap-2.5">
                {isPro ? (
                  <Icons.flash />
                ) : (
                  <div className="aspect-square size-1 rounded-full bg-white" />
                )}
                <div className="w-[177px] justify-start text-xs leading-[17px] font-medium text-white md:text-sm">
                  Total <span className="text-[#3dc13c]">{credits}</span>{" "}
                  credits
                </div>
              </div>
              <div className="flex-start gap-2.5">
                {isPro ? (
                  <Icons.flash />
                ) : (
                  <div className="aspect-square size-1 rounded-full bg-white" />
                )}
                <div className="text-xs leading-[17px] font-medium text-white md:text-sm">
                  {hasRollover ? (
                    <>
                      Credit Rollover (upto{" "}
                      <span className="text-[#3dc13c]">10,000 Credits</span>)
                    </>
                  ) : (
                    "No Credit Rollover"
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-start justify-start gap-2.5">
              <div className="text-base leading-tight font-semibold text-neutral-100">
                {isPro ? (
                  <>
                    Everything in free,{" "}
                    <span className="text-[#3dc13c]">Plus:</span>
                  </>
                ) : (
                  "Includes:"
                )}
              </div>
              <div className="flex flex-col items-start justify-start gap-1.5">
                {features.map((feature, index) => (
                  <PricingFeature key={index} {...feature} />
                ))}
              </div>
            </div>
          </div>
        </div>
        {!isPro ? (
          <Link
            href="image-generator"
            className="flex-center cursor-pointer self-stretch overflow-hidden rounded-[14px] py-3 text-center text-base leading-tight font-semibold text-white shadow-[inset_0px_4px_12px_0px_rgba(255,255,255,0.20)] outline-[1.50px] outline-offset-[-1.50px] outline-[#8c8c8c]"
          >
            Current Plan
          </Link>
        ) : (
          <button
            onClick={onSubscribe}
            disabled={subscriptionStatus === "active"}
            className="flex-center cursor-pointer self-stretch overflow-hidden rounded-[14px] bg-gradient-to-b from-[#6cb4ff]/60 to-[#1d7bdb]/60 py-3 text-center text-base leading-tight font-semibold text-white shadow-[0px_0px_8px_0px_rgba(30,123,219,0.30)] outline-[1.50px] outline-offset-[-1.50px] outline-[#6cb4ff] hover:from-[#6cb4ff]/80 hover:to-[#1d7bdb]/80"
          >
            Buy Credits
          </button>
        )}
      </div>
    </div>
  );
};

const PricingSection: FC = () => {
  const { user } = useUser();
  const router = useRouter();
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>("");
  const [sliderValue, setSliderValue] = useState(20);

  useEffect(() => {
    const fetchUserPlan = async () => {
      if (user) {
        try {
          const response = await axios.get("/api/user-plan");
          setSubscriptionStatus(response.data.subscriptionStatus);
        } catch (error) {
          console.error("Error fetching user plan:", error);
        }
      }
    };

    fetchUserPlan();
  }, [user]);

  const handleSubscribe = async () => {
    if (!user) {
      router.push("/sign-in");
      return;
    }

    const selectedTier =
      proPricingTiers.find((tier) => tier.price === sliderValue) ||
      proPricingTiers[0];

    const queryParams = new URLSearchParams({
      productId: selectedTier.planId,
      planType: "pro",
      planDuration: "monthly",
      amount: selectedTier.price.toString(),
      credits: selectedTier.credits.toString(),
    }).toString();

    router.push(`/checkout?${queryParams}`);
  };

  const freeFeatures = [
    { count: 10, label: "Flux, Leonardo images" },
    { count: 15, label: "Stable Diffusion Images" },
    { count: 1, label: "Seconds of Video Generation" },
    { count: 30, label: "Upscale & Background removal" },
  ];

  const proFeatures = [
    { count: sliderValue * 15, label: "Flux, Leonardo Images" },
    { count: sliderValue * 25, label: "Stable Diffusion Images" },
    { count: sliderValue * 6, label: "Seconds of Video Generation" },
    { count: sliderValue * 50, label: "Upscale & Background Removal" },
  ];

  return (
    <div className="flex w-full flex-col items-center gap-5 bg-black px-[12.5px] py-5 sm:py-16 md:pt-[53px] md:pb-[60px]">
      <div className="flex w-full flex-col items-center justify-start gap-[45px] md:gap-[34px]">
        <div className="flex w-full flex-col items-center justify-center gap-[15px] py-2.5 md:gap-2.5">
          <div className="text-center text-xl font-semibold max-md:max-w-[200px] md:text-[32px]">
            <span className="text-[#3DC13C]">Flexible Credits</span> For Your
            Creative Needs
          </div>
          <div className="text-center text-xs font-medium text-[#bfbfbf] max-md:max-w-[280px] md:text-lg">
            Buy credits as needed and use them anytime flexibility for your
            workflow
          </div>
        </div>
        <div className="mx-auto w-full max-w-[666px] font-medium max-md:hidden">
          <div className="flex-center mb-2.5 gap-2.5 overflow-hidden rounded-md px-5 py-1.5 text-center text-xs font-semibold text-neutral-100">
            Select credits
          </div>
          <div className="px-2">
            <Slider
              defaultValue={[10]}
              min={10}
              max={100}
              step={10}
              value={[sliderValue]}
              onValueChange={([value]) => setSliderValue(value)}
              className="mb-6"
            />
          </div>
          <div className="flex items-center justify-between py-2.5">
            {[1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000].map(
              (value) => (
                <div key={value} className="text-base text-[#f1f1f1]">
                  <div className="flex-center w-[24.8px]">{value}</div>
                </div>
              )
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse items-center justify-center gap-[30px] md:flex-row">
        <PricingCard
          type="free"
          price={0}
          credits={50}
          features={freeFeatures}
          isCurrentPlan={subscriptionStatus === "free"}
        />
        <PricingCard
          type="pro"
          price={sliderValue}
          credits={sliderValue * 100}
          features={proFeatures}
          hasRollover
          onSubscribe={handleSubscribe}
          subscriptionStatus={subscriptionStatus}
          setSliderValue={setSliderValue}
        />
      </div>
    </div>
  );
};

export default PricingSection;
